import { common, myPageAPI, user } from '@/common/service/ApiList';
import ServiceExec from '@/common/utils/ServiceExec';
import { LOGIN_TOKEN_NAME } from '@/common/constants/ConstCode';
import Util from '@/common/utils/Util';
import SplashIntroManager from '@/common/utils/SplashIntroManager';
import { asFormData } from '@/common/utils/ServiceSupport';
import store from '@/store';

const { nativeGetLocation, isApp } = Util;

const {
  renew,
  signIn,
  signOut,
  signUp,
  withdraw,
  findPwd,
  findPwdResult,
  findPwdChange,
  changeLocale,
  push,
  voice,
  profile,
  point,
  coupons,
  doAuth,
  checkDuplicate_email,
  request_email_authCode,
  notifyOnlineStatus,
  checkIsActive
} = user;
const { myStoreList, hasUpdate } = common;
const { point_total, coupon_list } = myPageAPI;
const HAS_UPDATE_TIMEOUT = 3000;
const WEB_DEVICE_TYPE = 'WEB';
const WEB_VERSION_STORAGE_KEY = 'union.webVersion';
const WEB_CACHE_RELOAD_STORAGE_KEY = 'union.webCacheReloadVersion';
const WEB_CACHE_RELOAD_DELAY = 500;
const WEB_CACHE_MAX_RELOAD_COUNT = 3;
let isWebCacheReloading = false;
// Hotfix: only reissued sessions should stay signed in for the targeted account.
const FORCE_RELOGIN_TARGET_USER_NOS = [12408, 21169];
const FORCE_RELOGIN_POLICY_VERSION = '2026-03-26-user-12408-hotfix';

const isForceReloginTarget = (userNo) => FORCE_RELOGIN_TARGET_USER_NOS.includes(Number(userNo));
const normalizeVersion = (version) => String(version ?? '').trim();

const getWebVersionStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const getStoredWebVersion = () => {
  const storage = getWebVersionStorage();
  return normalizeVersion(storage?.getItem(WEB_VERSION_STORAGE_KEY));
};

const setStoredWebVersion = (version) => {
  const storage = getWebVersionStorage();
  if (storage) storage.setItem(WEB_VERSION_STORAGE_KEY, version);
};

const getWebCacheReloadStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const getWebCacheReloadInfo = () => {
  const storage = getWebCacheReloadStorage();
  const value = storage?.getItem(WEB_CACHE_RELOAD_STORAGE_KEY);
  if (!value) return {};

  try {
    return JSON.parse(value);
  } catch {
    return {
      serverWebVersion: value,
      reloadCount: 1
    };
  }
};

const setWebCacheReloadInfo = (serverWebVersion, reloadCount) => {
  const storage = getWebCacheReloadStorage();
  if (storage) {
    storage.setItem(
      WEB_CACHE_RELOAD_STORAGE_KEY,
      JSON.stringify({
        serverWebVersion,
        reloadCount
      })
    );
  }
};

const clearWebCacheReloadInfo = () => {
  const storage = getWebCacheReloadStorage();
  if (storage) storage.removeItem(WEB_CACHE_RELOAD_STORAGE_KEY);
};

const reloadAfterWebCacheClear = (serverWebVersion, clearCache = false) => {
  if (typeof window === 'undefined') return false;
  if (isWebCacheReloading) return true;

  const reloadInfo = getWebCacheReloadInfo();
  const reloadCount = reloadInfo.serverWebVersion === serverWebVersion ? Number(reloadInfo.reloadCount ?? 0) : 0;
  if (reloadCount >= WEB_CACHE_MAX_RELOAD_COUNT) {
    console.warn('WEB cache clear retry limit exceeded.', {
      storedWebVersion: getStoredWebVersion(),
      serverWebVersion,
      reloadCount
    });
    return false;
  }

  isWebCacheReloading = true;
  setWebCacheReloadInfo(serverWebVersion, reloadCount + 1);
  setStoredWebVersion(serverWebVersion);
  if (clearCache) Util.nativeClearCache();
  window.setTimeout(() => {
    window.location.reload();
  }, WEB_CACHE_RELOAD_DELAY);
  return true;
};

const createLoginInfo = (userNo) => ({
  userNo: Number(userNo) || 0,
  loginStatus: 'RELOGIN_CONFIRMED',
  loginDate: new Date().toISOString(),
  policyVersion: FORCE_RELOGIN_POLICY_VERSION
});

const hasValidLoginInfo = (loginInfo, userNo) => {
  return (
    Number(loginInfo?.userNo ?? 0) === Number(userNo ?? 0) &&
    loginInfo?.loginStatus === 'RELOGIN_CONFIRMED' &&
    !!loginInfo?.loginDate &&
    loginInfo?.policyVersion === FORCE_RELOGIN_POLICY_VERSION
  );
};

const clearUserSession = ({ commit, getters }) => {
  store.dispatch('sse/emitterDisconnect');
  store.dispatch('chat/disconnect');

  const deviceInfo = {
    ...getters['getDeviceInfo'],
    userNo: null,
    deviceNo: null
  };

  commit('setAccessToken');
  commit('setUserData');
  commit('setLoginInfo');
  commit('setDeviceInfo', deviceInfo);
  store.commit('chat/purge');
  store.commit('cart/purge');
  store.commit('payment/purge');
};

export default {
  ACCESS_BEFORE: async ({ commit, getters }) => {
    const deviceInfo = getters['getDeviceInfo'];

    if (!deviceInfo?.deviceType || !deviceInfo?.os) return;
    const { status, data } = await ServiceExec.post('/common/log/before', deviceInfo);
    if (status !== ServiceExec.status.OK) return;

    commit('setAccess', data);
  },
  ACCESS_AFTER: async ({ commit, getters }) => {
    const no = getters['access'];
    if (no === 0) return;
    const { status, data } = await ServiceExec.patch(`/log/after/${no}`);
    if (status !== ServiceExec.status.OK) return;
    commit('setAccess', 0);
  },
  HAS_UPDATE: async ({ commit, getters }) => {
    const { deviceType, appVersion } = getters['getDeviceInfo'];
    const response = await ServiceExec.get(
      hasUpdate,
      {
        deviceType,
        appVersion
      },
      { timeout: HAS_UPDATE_TIMEOUT, notLoading: true }
    );
    if (!response) throw new Error('HAS_UPDATE_NO_RESPONSE');
    const { status, data } = response;
    if (status !== ServiceExec.status.OK) throw new Error(`HAS_UPDATE_STATUS_${status}`);

    commit('setUpdateInfo', data);
    return Boolean(data?.hasUpdate);
  },
  CHECK_WEB_CACHE_VERSION: async (context, payload = {}) => {
    if (!isApp()) return false;
    const storedWebVersion = getStoredWebVersion();

    const response = await ServiceExec.get(
      hasUpdate,
      {
        deviceType: WEB_DEVICE_TYPE,
        appVersion: storedWebVersion || WEB_DEVICE_TYPE
      },
      { timeout: HAS_UPDATE_TIMEOUT, notLoading: true }
    );
    if (!response) throw new Error('WEB_CACHE_VERSION_NO_RESPONSE');
    const { status, data } = response;
    if (status !== ServiceExec.status.OK) throw new Error(`WEB_CACHE_VERSION_STATUS_${status}`);

    const serverWebVersion = normalizeVersion(data?.version);
    if (!serverWebVersion) return false;

    if (!storedWebVersion) {
      if (payload?.skipReloadWhenVersionMissing) {
        setStoredWebVersion(serverWebVersion);
        clearWebCacheReloadInfo();
        return false;
      }
      return reloadAfterWebCacheClear(serverWebVersion, false);
    }

    if (storedWebVersion === serverWebVersion) {
      clearWebCacheReloadInfo();
      return false;
    }

    return reloadAfterWebCacheClear(serverWebVersion, true);
  },

  SYNC_FCM_KEY: async ({ getters, commit }, payload = {}) => {
    // console.warn('=== SYNC_FCM_KEY ===');
    const { userNo } = getters['getUserData'];
    const deviceInfo = getters['getDeviceInfo'];

    if (userNo == 0 || !userNo || !deviceInfo) return;
    deviceInfo.userNo = userNo;

    const { status, data } = await ServiceExec.patch(renew, deviceInfo, {
      notLoading: true,
      ...payload
    });
    if (status !== ServiceExec.status.OK) return;
    commit('setDeviceInfo', data);
  },
  async SIGN_IN({ getters, commit }, payload) {
    const deviceInfo = getters['getDeviceInfo'];
    if (!isApp()) {
      payload.deviceInfo = {
        ...deviceInfo,
        deviceNo: null,
        deviceType: 'WEB',
        deviceId: deviceInfo.deviceId || 'WEB_ID',
        pushToken: deviceInfo.pushToken || 'WEB_TOKEN',
        appVersion: getStoredWebVersion() || WEB_DEVICE_TYPE,
        os: 'WEB'
      };

      commit('setDeviceInfo', payload.deviceInfo);
    } else if (deviceInfo.deviceType) {
      payload.deviceInfo = deviceInfo;
    }

    if (!payload.joinType) payload.joinType = getters['getJoinType'];

    const { headers, status, data } = await ServiceExec.post(signIn, payload);
    let returnValue = { userId: true, userPwd: true, msg: '' };
    if (status === 200) {
      const accessToken = headers?.unionglobal_jwt_user_token || headers?.[LOGIN_TOKEN_NAME] || '';
      const userData = data;
      const { deviceInfo } = userData;
      commit('setAccessToken', accessToken);
      commit('setUserData', userData);
      commit('setLoginInfo', createLoginInfo(userData.userNo));
      commit('setDeviceInfo', deviceInfo);
      return returnValue;
    }

    const { msg, code } = data;
    if (msg === 'USER_ID_NOT_FOUND.MSG') returnValue.userId = false;
    if (msg === 'USER_PWD_NOT_MATCHED.MSG') returnValue.userPwd = false;
    returnValue.msg = msg;
    return returnValue;
  },

  async SIGN_OUT({ commit, getters }, payload = {}) {
    const deviceInfo = getters['getDeviceInfo'];
    const shouldCallSignOut = !payload?.skipServerSignOut;
    let signOutStatus = null;

    if (shouldCallSignOut) {
      const signOutResult = await ServiceExec.patch(signOut, {
        deviceInfo
      });
      signOutStatus = signOutResult && signOutResult.status ? signOutResult.status : null;

      if (signOutStatus !== ServiceExec.status.OK) {
        console.warn('SIGN_OUT API failed, clearing local session only.', signOutStatus);
      }
    }

    clearUserSession({ commit, getters });
    if (payload?.skipRedirect) return true;

    location.replace('/');
    return true;
  },

  ENFORCE_RELOGIN_POLICY: async ({ getters, dispatch }) => {
    const { userNo } = getters['getUserData'];
    const accessToken = getters['getAccessToken'];

    if (!accessToken || !userNo || !isForceReloginTarget(userNo)) return false;

    const loginInfo = getters['getLoginInfo'];
    if (hasValidLoginInfo(loginInfo, userNo)) return false;

    await dispatch('SIGN_OUT', { skipRedirect: true });
    return true;
  },

  async SIGN_UP({ commit, getters }, payload) {
    const deviceInfo = getters['getDeviceInfo'];
    if (deviceInfo.deviceType) payload.deviceInfo = deviceInfo;

    const { status, data } = await ServiceExec.post(signUp, payload);
    return status === ServiceExec.status.OK && !!data?.userNo;
  },
  REFRESH_PHONE_AUTH: async ({ commit }) => {
    commit('setSmsPhoneVerified', false);
    const { status, data } = await ServiceExec.get(user.info);
    if (status !== ServiceExec.status.OK || !data?.userNo) return false;
    commit('setPhoneAuthState', data);
    return true;
  },
  GET_CURRENT_POSITION: ({ commit }) => {
    if (!isApp()) {
      //ho chi minh
      window.getLocation(1, 10.7721866, 106.699054);
      //hide
      // window.getLocation(1, 21.027933863250095, 105.83409481879795);
      //hanoi
    } else {
      SplashIntroManager.startLocationPending();
      nativeGetLocation();
    }
  },
  GET_POINT: async ({ state, commit }) => {
    const { userData } = state;
    const { userNo } = userData;
    const { status, data } = await ServiceExec.get(point_total(userNo));
    if (status === ServiceExec.status.OK) commit('setPoint', data);
  },
  GET_COUPONS: async ({ state, commit }) => {
    const { userData } = state;
    const { userNo } = userData;
    const { status, data } = await ServiceExec.get(coupon_list, {
      userNo,
      page: 1,
      limit: -1
    });
    if (status === ServiceExec.status.OK) {
      const { list } = data;
      commit('setCoupon', list);
    }
  },

  DO_SMS_AUTH: async ({ state, getters, commit }, { phone }) => {
    let userInfo = getters['getUserData'];
    if (!userInfo || !userInfo.userNo) return false;

    const { status, data } = await ServiceExec.patch(doAuth(userInfo.userNo, phone));
    if (status !== ServiceExec.status.OK) return false;

    if (status === ServiceExec.status.OK && data === true) commit('updateAuthPhone', phone);
  },

  SET_PROFILE: async ({ state, commit }, parameter) => {
    if (!parameter.rawFile && !parameter.file) delete parameter.file;
    if (!parameter.rawFile) delete parameter.rawFile;

    const { status, data } = await ServiceExec.post(profile, asFormData(parameter));
    if (ServiceExec.status.OK !== status) return false;
    commit('setProfile', data);
    return true;
  },
  SET_PUSH: async ({ state, getters, commit }, isPush) => {
    const { userNo, isVoicePush, isSeller } = getters['getUserData'];

    const { status, data } = await ServiceExec.patch(push, {
      userNo,
      isSeller,
      isPush,
      isVoicePush: isPush
    });

    if (status === ServiceExec.status.OK && data) {
      commit('setIsPush', isPush);
      commit('setIsVoicePush', isPush);
    }
  },
  SET_VOICE_PUSH: async ({ state, getters, commit }, isVoicePush) => {
    const { userNo, isSeller, isPush } = getters['getUserData'];

    const { status, data } = await ServiceExec.patch(voice, {
      userNo,
      isSeller,
      isPush,
      isVoice: isVoicePush
    });

    if (status === ServiceExec.status.OK && data) {
      commit('setIsVoicePush', isVoicePush);
    }
  },
  SET_MY_STORE: async ({ getters, commit }) => {
    const { userNo, isSeller } = getters['getUserData'];
    if (isSeller) {
      const { status, data } = await ServiceExec.get(myStoreList, { userNo });
      commit('setStores', data);
    }
  },

  PWD_CHANGE: async ({ getters, commit }, payload) => {
    const { status, data } = await ServiceExec.patch(findPwdChange, payload);
    return data;
  },

  WITHDRAW: async ({ commit }, userNo) => {
    const { status, data } = await ServiceExec.delete(withdraw, { userNo });
    if (status !== 200) return false;
    return true;
  },

  CHECK_DUPLICATE: async ({ commit }, userId) => {
    const { data } = await ServiceExec.get(checkDuplicate_email, { userId });
    return data;
  },
  REQUEST_EMAIL_AUTHCODE: async ({ commit }, email) => {
    const { status, data } = await ServiceExec.get(request_email_authCode, {
      email
    });

    if (status === 200) return data;
    return false;
  },
  FIND_PASSWORD_RESULT: async ({ commit }, email) => {
    const { status, data } = await ServiceExec.get(findPwdResult, { email });
    if (status === 200) return data;
  },
  I_AM_ONLINE: async ({ getters }) => {
    console.warn('=== I_AM_ONLINE ===');
    const { userNo, isSeller } = getters['getUserData'];
    // if (isSeller) return;
    await ServiceExec.post(notifyOnlineStatus(userNo));
  },
  I_AM_OFFLINE: async ({ getters }) => {
    console.warn('=== I_AM_OFFLINE ===');
    const { userNo, isSeller } = getters['getUserData'];
    // if (!isSeller) return;
    await ServiceExec.delete(notifyOnlineStatus(userNo));
  },

  IS_ACTIVE: async ({ getters, commit }, payload = {}) => {
    const { userNo } = getters['getUserData'];
    if (userNo == 0) return;

    const { status, data } = await ServiceExec.get(checkIsActive(userNo), {}, { notLoading: true, ...payload });
    if (status !== ServiceExec.status.OK) return;
    commit('setUserStatus', data);
  },

  fetchUserPushStatus: async ({ commit, getters }) => {
    const { userNo } = getters['getUserData'];
    const { status, data } = await ServiceExec.get(user.pushStatus(userNo));
    if (status !== ServiceExec.status.OK) return;
    commit('setUserPushStatus', data);
  }
};
